'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'

export const MAPS_CONSENT_KEY = 'sb_maps_consent'

type Props = {
  embedUrl: string
  title: string
  /** Min height (px) from lg up, same as the old iframe. */
  heightPx?: number
  directionsUrl?: string | null
}

/**
 * Click-to-load Google Maps. No iframe is in the DOM (or SSR HTML) until the
 * visitor opts in, so Google receives no IP before consent. Separate from the
 * analytics consent (`sb_cookie_consent`).
 */
export function MapConsentEmbed({ embedUrl, title, heightPx = 420, directionsUrl }: Props) {
  const t = useTranslations('mapConsent')
  const [accepted, setAccepted] = useState(false)

  useEffect(() => {
    try {
      if (localStorage.getItem(MAPS_CONSENT_KEY) === 'accepted') setAccepted(true)
    } catch {}
  }, [])

  // Footer "Cookie-Einstellungen": revoke the map consent too.
  useEffect(() => {
    const revoke = () => {
      try {
        localStorage.removeItem(MAPS_CONSENT_KEY)
      } catch {}
      setAccepted(false)
    }
    window.addEventListener('open-cookie-settings', revoke)
    return () => window.removeEventListener('open-cookie-settings', revoke)
  }, [])

  function load() {
    try {
      localStorage.setItem(MAPS_CONSENT_KEY, 'accepted')
    } catch {}
    setAccepted(true)
  }

  const frame = 'aspect-[16/11] w-full lg:aspect-auto lg:h-full'

  if (accepted) {
    return (
      <div className={frame}>
        <iframe
          src={embedUrl}
          title={title}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          className="block h-full w-full lg:min-h-[var(--map-h)]"
          style={{ border: 0, ['--map-h' as string]: `${heightPx}px` }}
        />
      </div>
    )
  }

  return (
    <div
      className={`${frame} flex flex-col items-center justify-center gap-4 bg-neutral-100 p-6 text-center lg:min-h-[var(--map-h)] dark:bg-white/5`}
      style={{ ['--map-h' as string]: `${heightPx}px` }}
    >
      <p className="max-w-sm text-sm leading-relaxed text-neutral-700 dark:text-white/70">
        {t('text')}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center justify-center rounded-lg bg-neutral-900 px-5 py-2.5 font-mono text-sm uppercase tracking-wider text-white shadow-sm transition-opacity hover:opacity-85 dark:border dark:border-white/10 dark:bg-black dark:text-[var(--neon-orange)]"
        >
          {t('load')}
        </button>
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm underline underline-offset-4 text-neutral-900 dark:text-white"
          >
            {t('route')}
          </a>
        )}
      </div>
    </div>
  )
}
